import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { auth } from "@/api/auth";
import { Button, Field, Input, Notice } from "@/components/ui/Field";
import { AuthCard } from "@/features/auth/AuthLayout";
import { SocialButtons } from "@/features/auth/SocialButtons";
import { applyAuthError, type AuthFailure } from "@/features/auth/formErrors";
import { fieldMessage } from "@/lib/fieldMessage";
import { signInSchema, type SignInValues } from "@/features/auth/schemas";
import { sessionChanged } from "@/features/auth/session";

export function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Where the guard wanted to go before it sent us here.
  const { redirect } = useSearch({ from: "/auth/sign-in" });
  const [failure, setFailure] = useState<AuthFailure>({});
  const [resent, setResent] = useState(false);

  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFailure({});
    try {
      await auth.signIn(values.email, values.password);
      await sessionChanged(queryClient);
      await navigate({ to: redirect ?? "/" });
    } catch (error) {
      setFailure(
        applyAuthError(error, {
          setError: form.setError,
          fields: ["email", "password"],
          fallback: t("auth.errors.signInFailed"),
        }),
      );
    }
  });

  // Signing in with an unverified address is not a wrong password; say so, and offer the link again.
  if (failure.pendingFlow === "verify_email") {
    return (
      <AuthCard title={t("auth.verify.neededTitle")} description={t("auth.verify.neededBody")}>
        {resent ? (
          <Notice tone="success">{t("auth.verify.resent")}</Notice>
        ) : (
          <Button
            type="button"
            variant="secondary"
            onClick={async () => {
              await auth.resendVerification(form.getValues("email")).catch(() => undefined);
              setResent(true);
            }}
          >
            {t("auth.verify.resend")}
          </Button>
        )}
        <Link to="/auth/sign-in" className="text-brand-700 block text-center text-sm underline">
          {t("auth.backToSignIn")}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t("auth.signIn.title")}>
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field
          name="email"
          label={t("auth.email")}
          error={fieldMessage(form.formState.errors, "email", t)}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            invalid={!!form.formState.errors.email}
            {...form.register("email")}
          />
        </Field>
        <Field
          name="password"
          label={t("auth.password")}
          error={fieldMessage(form.formState.errors, "password", t)}
        >
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            invalid={!!form.formState.errors.password}
            {...form.register("password")}
          />
        </Field>
        {failure.formError && <Notice tone="error">{failure.formError}</Notice>}
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("auth.signIn.working") : t("auth.signIn.submit")}
        </Button>
      </form>

      <SocialButtons next={redirect ?? "/"} />

      <div className="space-y-2 text-center text-sm text-stone-600">
        <Link to="/auth/forgot-password" className="text-brand-700 block underline">
          {t("auth.forgot.link")}
        </Link>
        <p>
          {t("auth.signIn.noAccount")}{" "}
          <Link to="/auth/sign-up" className="text-brand-700 underline">
            {t("auth.signUp.submit")}
          </Link>
        </p>
      </div>
    </AuthCard>
  );
}
